import {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
  type ChangeEvent
} from 'react';
import { useQueryState, parseAsInteger, createParser } from 'nuqs';
import { useDebouncedCallback } from 'use-debounce';
import useEventsQuery from '@/hooks/api/events/useEventsQuery';
import useMap from '@/hooks/useMap';
import { isErrorResponse } from '@/services/typeguards';
import { getBoundsFromPoints } from '@/services/geo';
import {
  MapCameraChangedEvent,
  useApiIsLoaded
} from '@vis.gl/react-google-maps';
import {
  toUtcBoundary,
  dateTimeStringToDate,
  getTodayDateString
} from '@/services/dateTime';
import {
  placeToCityLocation,
  reverseGeocodeCity,
  type CityLocation
} from '@/services/integrations/googleMaps';
import { DEFAULT_CENTER } from '@goodneighbor/api/services/supportedCities';

export { DEFAULT_CENTER };

const DEFAULT_RADIUS_METERS =
  process.env.NEXT_PUBLIC_DEFAULT_RADIUS_METERS?.trim() || '100000';

const multipleStringParser = createParser<string[]>({
  parse: (value) =>
    value
      ? value
          .split(',')
          .map((cause) => cause.trim())
          .filter(Boolean)
      : [],
  serialize: (value) => value.join(',')
}).withDefault([]);

type DateField = 'start' | 'end';

type HandleDateChangeInput = {
  currentStart: string | null;
  currentEnd: string | null;
  field: DateField;
  value: string | null;
};

type HandleDateChangeResult = {
  startDate: string | null;
  endDate: string | null;
};

export const handleDateChange = ({
  currentStart,
  currentEnd,
  field,
  value
}: HandleDateChangeInput): HandleDateChangeResult => {
  let nextStart = field === 'start' ? value : currentStart;
  let nextEnd = field === 'end' ? value : currentEnd;

  if (!nextStart && !nextEnd) {
    return { startDate: null, endDate: null };
  }

  if (nextStart && !nextEnd && value !== null) {
    nextEnd = nextStart;
  }

  if (nextStart && nextEnd) {
    const startDateObj = dateTimeStringToDate(nextStart);
    const endDateObj = dateTimeStringToDate(nextEnd);

    if (startDateObj && endDateObj && startDateObj > endDateObj) {
      if (field === 'start') {
        nextEnd = nextStart;
      } else {
        nextStart = nextEnd;
      }
    }
  }

  return { startDate: nextStart, endDate: nextEnd };
};

export function useEvents() {
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedEventId, setSelectedEventId] = useQueryState('eventId', {
    defaultValue: null,
    parse: (value) => value || null
  });
  const [page, setPage] = useQueryState('page', parseAsInteger.withDefault(1));
  const [centerLat, setCenterLat] = useQueryState('centerLat', {
    defaultValue: null,
    parse: (value) => value || null
  });
  const [centerLng, setCenterLng] = useQueryState('centerLng', {
    defaultValue: null,
    parse: (value) => value || null
  });
  const [north, setNorth] = useQueryState('north', {
    defaultValue: null,
    parse: (value) => value || null
  });
  const [south, setSouth] = useQueryState('south', {
    defaultValue: null,
    parse: (value) => value || null
  });
  const [east, setEast] = useQueryState('east', {
    defaultValue: null,
    parse: (value) => value || null
  });
  const [west, setWest] = useQueryState('west', {
    defaultValue: null,
    parse: (value) => value || null
  });
  const [startDate, setStartDate] = useQueryState('startDate', {
    defaultValue: null,
    parse: (value) => value || null
  });
  const [endDate, setEndDate] = useQueryState('endDate', {
    defaultValue: null,
    parse: (value) => value || null
  });

  const hasInitializedDateRef = useRef(false);
  useEffect(() => {
    if (!hasInitializedDateRef.current) {
      hasInitializedDateRef.current = true;
      if (startDate === null) {
        setStartDate(getTodayDateString());
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [volunteersMin, setVolunteersMin] = useQueryState('volunteersMin', {
    defaultValue: null,
    parse: (value) => value || null
  });
  const [volunteersMax, setVolunteersMax] = useQueryState('volunteersMax', {
    defaultValue: null,
    parse: (value) => value || null
  });
  const [causeFiltersRaw, setCauseFilters] = useQueryState(
    'causes',
    multipleStringParser
  );
  const [activityTypesRaw, setActivityTypeFilters] = useQueryState(
    'activityTypes',
    multipleStringParser
  );
  const [skillTypesRaw, setSkillTypeFilters] = useQueryState(
    'skillTypes',
    multipleStringParser
  );
  const causeFilters = useMemo(() => causeFiltersRaw ?? [], [causeFiltersRaw]);
  const activityTypeFilters = useMemo(
    () => activityTypesRaw ?? [],
    [activityTypesRaw]
  );
  const skillTypeFilters = useMemo(() => skillTypesRaw ?? [], [skillTypesRaw]);
  const [includeClosedEvents, setIncludeClosedEvents] = useQueryState(
    'includeClosed',
    {
      defaultValue: 'false',
      parse: (value) => value || 'false',
      clearOnDefault: false
    }
  );
  const [isManualDrag, setIsManualDrag] = useState(false);
  const { setMarkerRef, geolocationLoading, location, mapPrimitive } = useMap();
  const isMapsApiLoaded = useApiIsLoaded();
  const popupRef = useRef<HTMLDivElement>(null);
  const programmaticMoveRef = useRef(false);
  const [centerPoint, setCenterPoint] = useState<{
    lat: string;
    lng: string;
  } | null>(centerLat && centerLng ? { lat: centerLat, lng: centerLng } : null);
  const [bounds, setBoundsState] = useState<{
    north: string;
    south: string;
    east: string;
    west: string;
  } | null>(
    north && south && east && west ? { north, south, east, west } : null
  );
  const [selectedCity, setSelectedCityState] = useState<CityLocation | null>(
    null
  );
  const [locationError, setLocationError] = useState<string | null>(null);
  const [locationLocked, setLocationLocked] = useState(
    Boolean(centerLat && centerLng) || Boolean(north && south && east && west)
  );

  const setBounds = useCallback(
    (
      newBounds: {
        north: string;
        south: string;
        east: string;
        west: string;
      } | null
    ) => {
      setBoundsState(newBounds);
      if (newBounds) {
        setNorth(newBounds.north);
        setSouth(newBounds.south);
        setEast(newBounds.east);
        setWest(newBounds.west);
      } else {
        setNorth(null);
        setSouth(null);
        setEast(null);
        setWest(null);
      }
    },
    [setNorth, setSouth, setEast, setWest]
  );

  const setCenterPointDebounced = useDebouncedCallback(setCenterPoint, 1000);
  const setPageDebounced = useDebouncedCallback(setPage, 1000);

  const [searchQuery, setSearchQuery] = useQueryState('q', {
    defaultValue: '',
    parse: (value) => value || ''
  });
  const [searchValue, setSearchValue] = useState(searchQuery);
  const [locationValue, setLocationValue] = useState('');

  const updateBoundsFromMap = useCallback(() => {
    if (!mapPrimitive) {
      return;
    }

    const nextBounds = mapPrimitive.getBounds();
    if (!nextBounds) {
      return;
    }

    const northEast = nextBounds.getNorthEast();
    const southWest = nextBounds.getSouthWest();

    setBounds({
      north: northEast.lat().toString(),
      east: northEast.lng().toString(),
      south: southWest.lat().toString(),
      west: southWest.lng().toString()
    });
  }, [mapPrimitive, setBounds]);

  const updateBoundsFromMapDebounced = useDebouncedCallback(
    () => updateBoundsFromMap(),
    300
  );

  useEffect(() => {
    return () => {
      updateBoundsFromMapDebounced.cancel();
      setCenterPointDebounced.cancel();
      setPageDebounced.cancel();
    };
  }, [updateBoundsFromMapDebounced, setCenterPointDebounced, setPageDebounced]);

  useEffect(() => {
    if (searchQuery && searchValue !== searchQuery) {
      setSearchValue(searchQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  useEffect(() => {
    if (north && south && east && west && !bounds) {
      setBoundsState({ north, south, east, west });
      setLocationLocked(true);
    }
    if (centerLat && centerLng && !centerPoint) {
      setCenterPoint({ lat: centerLat, lng: centerLng });
      setLocationLocked(true);
    }
  }, [north, south, east, west, centerLat, centerLng, bounds, centerPoint]);

  const applyCityLocation = useCallback(
    (city: CityLocation) => {
      setSelectedCityState(city);
      setLocationLocked(true);
      setLocationValue(city.displayName);
      setLocationError(null);
      setIsManualDrag(false);

      updateBoundsFromMapDebounced.cancel();

      if (city.bounds) {
        setBounds({
          north: city.bounds.north.toString(),
          south: city.bounds.south.toString(),
          east: city.bounds.east.toString(),
          west: city.bounds.west.toString()
        });
      } else {
        setBounds(null);
      }

      setCenterPoint({
        lat: city.center.lat.toString(),
        lng: city.center.lng.toString()
      });
      setPage(1);

      if (mapPrimitive && google?.maps?.LatLngBounds) {
        programmaticMoveRef.current = true;
        if (city.bounds) {
          const nextBounds = new google.maps.LatLngBounds();
          nextBounds.extend(
            new google.maps.LatLng(city.bounds.north, city.bounds.east)
          );
          nextBounds.extend(
            new google.maps.LatLng(city.bounds.south, city.bounds.west)
          );
          mapPrimitive.fitBounds(nextBounds);
        } else {
          mapPrimitive.setCenter(city.center);
          mapPrimitive.setZoom(12);
        }
      }
    },
    [mapPrimitive, setPage, updateBoundsFromMapDebounced, setBounds]
  );

  useEffect(() => {
    if (!location || !isMapsApiLoaded || locationLocked || selectedCity) {
      return;
    }

    let isCancelled = false;

    reverseGeocodeCity({
      lat: location.coords.latitude,
      lng: location.coords.longitude
    }).then((cityLocation) => {
      if (cityLocation && !isCancelled) {
        applyCityLocation(cityLocation);
      }
    });

    return () => {
      isCancelled = true;
    };
  }, [
    location,
    isMapsApiLoaded,
    locationLocked,
    selectedCity,
    applyCityLocation
  ]);

  const handleSearchChange = (e: string | ChangeEvent<HTMLInputElement>) => {
    const value = typeof e === 'string' ? e : e.target.value;
    setSearchValue(value);
  };

  const handleSearchSubmit = () => {
    const trimmed = searchValue.trim();
    setSearchQuery(trimmed);
    setPage(1);
    setIsManualDrag(false);

    if (selectedCity) {
      applyCityLocation(selectedCity);
    }

    setTimeout(() => {
      refetch();
    }, 0);
  };

  const handleLocationChange = (e: string | ChangeEvent<HTMLInputElement>) => {
    const value = typeof e === 'string' ? e : e.target.value;
    setLocationValue(value);
    setLocationError(null);
  };

  const handleLocationBlur = () => {
    if (!locationValue.trim() && selectedCity?.displayName) {
      setLocationValue(selectedCity.displayName);
      setLocationError(null);
    }
  };

  const handleLocationSelect = (
    place: google.maps.GeocoderResult | google.maps.places.PlaceResult
  ) => {
    const cityLocation = placeToCityLocation(place);

    if (!cityLocation) {
      setLocationError('Please select a city, not a street address.');
      return;
    }

    applyCityLocation(cityLocation);
  };

  const startDateUtc = startDate
    ? toUtcBoundary(startDate, 'start')
    : undefined;
  const endDateUtc = endDate ? toUtcBoundary(endDate, 'end') : undefined;

  const handleVolunteersNeededChange = (
    field: 'min' | 'max',
    value: string | number | ChangeEvent<HTMLInputElement>
  ) => {
    const rawValue =
      typeof value === 'string'
        ? value
        : typeof value === 'number'
          ? value.toString()
          : (value?.target?.value ?? '');
    const sanitized = rawValue.replace(/[^0-9]/g, '');
    const nextValue = sanitized ? sanitized : null;

    if (field === 'min') {
      setVolunteersMin(nextValue);
    } else {
      setVolunteersMax(nextValue);
    }

    setPage(1);
  };

  const eventsQueryInput = useMemo(() => {
    const causeTypesQuery =
      causeFilters.length > 0 ? causeFilters.join(',') : undefined;
    const activityTypesQuery =
      activityTypeFilters.length > 0
        ? activityTypeFilters.join(',')
        : undefined;
    const skillTypesQuery =
      skillTypeFilters.length > 0 ? skillTypeFilters.join(',') : undefined;

    return {
      page: String(page),
      north: bounds?.north,
      south: bounds?.south,
      east: bounds?.east,
      west: bounds?.west,
      centerLat: bounds ? undefined : centerPoint?.lat,
      centerLng: bounds ? undefined : centerPoint?.lng,
      radius: bounds ? undefined : DEFAULT_RADIUS_METERS,
      q: searchQuery || undefined,
      startDate: startDateUtc,
      endDate: endDateUtc,
      causeTypes: causeTypesQuery,
      activityTypes: activityTypesQuery,
      skillTypes: skillTypesQuery,
      volunteersMin: volunteersMin || undefined,
      volunteersMax: volunteersMax || undefined,
      registrationOpen: includeClosedEvents === 'true' ? 'false' : 'true'
    } as const;
  }, [
    bounds,
    centerPoint,
    page,
    searchQuery,
    startDateUtc,
    endDateUtc,
    causeFilters,
    activityTypeFilters,
    skillTypeFilters,
    volunteersMin,
    volunteersMax,
    includeClosedEvents
  ]);

  const { data, isLoading, refetch } = useEventsQuery(
    eventsQueryInput,
    Boolean(bounds || centerPoint)
  );

  const events = useMemo(() => {
    if (!data || isErrorResponse(data)) {
      return [];
    }
    return data.data.events;
  }, [data]);

  const selectedEvent = useMemo(() => {
    if (!events || events.length === 0 || !selectedEventId) {
      return null;
    }
    return events.find((event) => event.id === selectedEventId) || null;
  }, [events, selectedEventId]);

  useEffect(() => {
    if (
      !data ||
      isErrorResponse(data) ||
      data.data.events.length === 0 ||
      isManualDrag ||
      !google.maps.LatLngBounds
    ) {
      return;
    }
    programmaticMoveRef.current = true;
    const boundsFromPoints = getBoundsFromPoints(
      data.data.events.map((event) => ({
        lat: event?.coords?.coordinates[1] || 0,
        lng: event?.coords?.coordinates[0] || 0
      }))
    );
    const bounds = new google.maps.LatLngBounds();
    bounds.extend(
      new google.maps.LatLng(boundsFromPoints.north, boundsFromPoints.east)
    );
    bounds.extend(
      new google.maps.LatLng(boundsFromPoints.south, boundsFromPoints.west)
    );
    mapPrimitive?.fitBounds(bounds);
  }, [data, mapPrimitive, isManualDrag]);

  const meta = useMemo(() => {
    if (!data || isErrorResponse(data)) {
      return {
        pagination: {
          perPage: 10,
          currentPage: page,
          previousPage: null,
          nextPage: null,
          lastPage: 1,
          total: 0
        }
      };
    }
    return data.data.meta;
  }, [data, page]);

  const availableCauseFilters = useMemo(() => {
    if (!data || isErrorResponse(data)) {
      return [] as string[];
    }

    const filterMeta = data.data.meta?.filter as
      | { causeTypes?: string[] }
      | undefined;
    const metaCauseTypes = Array.isArray(filterMeta?.causeTypes)
      ? filterMeta?.causeTypes.filter((cause): cause is string =>
          Boolean(cause)
        )
      : [];

    const eventCauseTypes = events
      .map((event) => event.causeType?.trim())
      .filter((cause): cause is string => Boolean(cause));

    return Array.from(new Set([...metaCauseTypes, ...eventCauseTypes]));
  }, [data, events]);

  const [availableActivityTypeIds, activityTypes]: [
    string[],
    { id: string; name: string }[]
  ] = useMemo(() => {
    if (!data || isErrorResponse(data)) {
      return [[], []];
    }
    const filterMeta = data.data.meta?.filter as
      | { activityTypes?: { id: string; name: string }[] }
      | undefined;
    const metaActivityTypes = Array.isArray(filterMeta?.activityTypes)
      ? filterMeta?.activityTypes.filter(
          (activityType): activityType is { id: string; name: string } =>
            Boolean(activityType.id) && Boolean(activityType.name)
        )
      : [];
    return [
      metaActivityTypes.map((activityType) => activityType.id),
      metaActivityTypes
    ];
  }, [data]);

  const [availableSkillTypeIds, skillTypes]: [
    string[],
    { id: string; name: string }[]
  ] = useMemo(() => {
    if (!data || isErrorResponse(data)) {
      return [[], []];
    }
    const filterMeta = data.data.meta?.filter as
      | { skillTypes?: { id: string; name: string }[] }
      | undefined;
    const metaSkillTypes = Array.isArray(filterMeta?.skillTypes)
      ? filterMeta?.skillTypes.filter(
          (skillType): skillType is { id: string; name: string } =>
            Boolean(skillType.id) && Boolean(skillType.name)
        )
      : [];
    return [metaSkillTypes.map((skillType) => skillType.id), metaSkillTypes];
  }, [data]);

  const displayedCauseFilters = useMemo(() => {
    const combined = new Set(availableCauseFilters);
    causeFilters.forEach((cause) => {
      if (cause) {
        combined.add(cause);
      }
    });
    return Array.from(combined);
  }, [availableCauseFilters, causeFilters]);

  const setSelectedEvent = (
    args: {
      id: string;
    } | null
  ) => {
    if (!args) {
      setSelectedEventId(null);
      setCenterLat(null);
      setCenterLng(null);
      return;
    }
    const { id } = args;
    setSelectedEventId(id);
    setCenterLat(centerPoint?.lat || '');
    setCenterLng(centerPoint?.lng || '');
  };

  const resetToLocation = (
    lat: number,
    lng: number,
    bounds?: { north: number; south: number; east: number; west: number }
  ) => {
    setIsManualDrag(false);
    setPage(1);
    setSelectedCityState(null);
    setLocationLocked(true);
    setLocationError(null);
    setLocationValue('');
    setCenterPoint({
      lat: lat.toString(),
      lng: lng.toString()
    });

    setSearchValue('');
    setSearchQuery(null);
    setStartDate(getTodayDateString());
    setEndDate(null);
    setCauseFilters(null);
    setVolunteersMin(null);
    setVolunteersMax(null);
    setIncludeClosedEvents('false');

    if (bounds) {
      setBounds({
        north: bounds.north.toString(),
        south: bounds.south.toString(),
        east: bounds.east.toString(),
        west: bounds.west.toString()
      });
    } else {
      setBounds(null);
    }

    if (mapPrimitive && google?.maps?.LatLngBounds) {
      programmaticMoveRef.current = true;
      if (bounds) {
        const nextBounds = new google.maps.LatLngBounds();
        nextBounds.extend(new google.maps.LatLng(bounds.north, bounds.east));
        nextBounds.extend(new google.maps.LatLng(bounds.south, bounds.west));
        mapPrimitive.fitBounds(nextBounds);
      } else {
        mapPrimitive.setCenter({ lat, lng });
        mapPrimitive.setZoom(12);
      }
    }

    setTimeout(() => {
      refetch();
    }, 0);
  };

  const handleMapDrag = () => {
    setIsManualDrag(true);
  };

  const handleMapBoundsChanged = (event: MapCameraChangedEvent) => {
    if (popupRef.current) {
      popupRef.current.style.display = 'none';
    }

    if (programmaticMoveRef.current) {
      programmaticMoveRef.current = false;
      updateBoundsFromMapDebounced.cancel();
      return;
    }

    setIsManualDrag(true);
    updateBoundsFromMapDebounced();

    const center = event.detail.center;
    setCenterPointDebounced({
      lat: center.lat.toString(),
      lng: center.lng.toString()
    });
    setPageDebounced(1);
  };

  const applyDateChange = (field: DateField, value: string | null) => {
    const { startDate: nextStart, endDate: nextEnd } = handleDateChange({
      currentStart: startDate,
      currentEnd: endDate,
      field,
      value
    });

    setStartDate(nextStart);
    setEndDate(nextEnd);

    setPage(1);
  };

  const clearDateFilters = () => {
    setStartDate(getTodayDateString());
    setEndDate(null);
    setCauseFilters(null);
    setActivityTypeFilters(null);
    setSkillTypeFilters(null);
    setVolunteersMin(null);
    setVolunteersMax(null);
    setIncludeClosedEvents('false');
    setPage(1);
  };

  const toggleIncludeClosedEvents = () => {
    setIncludeClosedEvents((current) =>
      current === 'true' ? 'false' : 'true'
    );
    setPage(1);
  };

  const toggleCauseFilter = (cause: string) => {
    if (!cause) {
      return;
    }

    setCauseFilters((current) => {
      if (!current || !current.length) {
        return [cause];
      }

      if (current.includes(cause)) {
        const next = current.filter((value) => value !== cause);
        return next.length ? next : null;
      }

      return [...current, cause];
    });

    setPage(1);
  };

  const toggleActivityTypeFilter = (activityType: string) => {
    if (!activityType) {
      return;
    }

    setActivityTypeFilters((current) => {
      if (!current || !current.length) {
        return [activityType];
      }

      if (current.includes(activityType)) {
        const next = current.filter((value) => value !== activityType);
        return next.length ? next : null;
      }

      return [...current, activityType];
    });

    setPage(1);
  };

  const toggleSkillTypeFilter = (skillType: string) => {
    if (!skillType) {
      return;
    }

    setSkillTypeFilters((current) => {
      if (!current || !current.length) {
        return [skillType];
      }

      if (current.includes(skillType)) {
        const next = current.filter((value) => value !== skillType);
        return next.length ? next : null;
      }

      return [...current, skillType];
    });

    setPage(1);
  };

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (causeFilters.length > 0) count++;
    if (activityTypeFilters.length > 0) count++;
    if (skillTypeFilters.length > 0) count++;
    if (volunteersMin) count++;
    if (volunteersMax) count++;
    if (includeClosedEvents === 'true') count++;
    return count;
  }, [
    causeFilters,
    activityTypeFilters,
    skillTypeFilters,
    volunteersMin,
    volunteersMax,
    includeClosedEvents
  ]);

  return {
    viewMode,
    setViewMode,
    searchValue,
    handleSearchChange,
    events,
    meta,
    activeFilterCount,
    isLoading,
    geolocationLoading,
    selectedEvent,
    selectedEventId,
    setSelectedEvent,
    location,
    setMarkerRef,
    mapPrimitive,
    popupRef,
    isManualDrag,
    setIsManualDrag,
    centerPoint,
    setCenterPoint,
    handleMapDrag,
    handleMapBoundsChanged,
    page,
    setPage,
    resetToLocation,
    startDate,
    endDate,
    handleDateChange: applyDateChange,
    clearFilters: clearDateFilters,
    causeFilters,
    toggleCauseFilter,
    availableCauseFilters: displayedCauseFilters,
    availableActivityTypeIds,
    activityTypes,
    activityTypeFilters,
    toggleActivityTypeFilter,
    skillTypeFilters,
    toggleSkillTypeFilter,
    availableSkillTypeIds,
    skillTypes,
    volunteersMin,
    volunteersMax,
    handleVolunteersNeededChange,
    locationValue,
    handleLocationChange,
    handleLocationSelect,
    handleLocationBlur,
    locationError,
    handleSearchSubmit,
    includeClosedEvents: includeClosedEvents === 'true',
    toggleIncludeClosedEvents
  };
}
